import React, { useContext, useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { getBookingConfig } from "../../../../Modules/Bookings/Requests/BookingConfigRequests";
import { dateForStateFromDateInput } from "../../../../Helpers/DateTimeHelper";
import { useDispatch } from "react-redux";
import { validateInterval } from "../../../../Common/Availability/AvailabilityValidation";

export const ReschedulerFormContext = React.createContext();

export const useReschedulerForm = () => {
    const context = useContext(ReschedulerFormContext);
    if (context === undefined) {
        throw new Error('useReschedulerForm hook must be used within a ReschedulerFormProvider');
    }
    return context;
};

const ReschedulerFormProvider = ({children, bookingConfig, occurrence, rescheduledOccurrences, onReschedule}) => {
    const dispatch = useDispatch();

    const [isShown, setShown] = useState(false);

    const [localBookingConfig, setLocalBookingConfig] = useState(null);
    const [localBoundary, setLocalBoundary] = useState(null);

    const [rescheduledAmenityId, setRescheduledAmenityId] = useState(bookingConfig.amenity_id);
    const [rescheduledAmenity, setRescheduledAmenity] = useState(null)
    const [rescheduledDate, setRescheduledDate] = useState((occurrence.start) ? occurrence.start.substring(0, 10) : '');

    const [rescheduledInterval, setRescheduledInterval] = useState({start: null, end: null});

    const occurrenceKey = occurrence.start;
    const rescheduledOccurrence = rescheduledOccurrences[occurrenceKey] ?? null;

    // Refresh the booking config when relevant params change
    useEffect(async () => {

        // Needs to be told when the modal in question is shown, otherwise every
        // recurrence will request a booking config as soon as the row is rendered
        if (!isShown) {
            return;
        }

        const result = await dispatch(
            getBookingConfig(
                bookingConfig.category_id,
                rescheduledAmenityId,
                bookingConfig.unit_id,
                dateForStateFromDateInput(rescheduledDate),
                localBoundary
            )
        );
        if (result) {
            setRescheduledAmenity(result.amenity);
            setLocalBookingConfig(result.config);
            maybeSetRescheduledTimeOnBookingConfigChange(result.config);
        }
    }, [rescheduledAmenityId, rescheduledDate, localBoundary, isShown]);

    const maybeSetRescheduledTimeOnBookingConfigChange = (bookingConfig) => {

        // If we've already set a rescheduled interval, don't reset it on booking config refresh
        if (rescheduledInterval.start) {
            return;
        }

        let originalRecurrence = {start: occurrence.start, end: occurrence.end};
        const {isValid, errors} = validateInterval(originalRecurrence, bookingConfig, bookingConfig.availability);
        if (isValid) {
            handleRescheduledIntervalChange(originalRecurrence)
        } else {
            console.log('interval error', errors)
            handleRescheduledIntervalChange(null);
        }
    }

    const handleRescheduledIntervalChange = (newInterval) => {
        setRescheduledInterval({
            start: newInterval?.start ?? null,
            end: newInterval?.end ?? null,
        });
    }

    const handleConfirm = () => {
        onReschedule(
            occurrenceKey,
            {
                'amenity_id': rescheduledAmenityId,
                'start': rescheduledInterval.start,
                'end': rescheduledInterval.end,
            }
        )
    }

    const handleRemove = () => {
        onReschedule(occurrenceKey, false);
    }

    return (<>
        <ReschedulerFormContext.Provider
            value={{
                mainBookingConfig: bookingConfig,

                localBookingConfig,
                occurrence,
                rescheduledOccurrence,

                rescheduledAmenityId,
                rescheduledAmenity,
                rescheduledDate,

                rescheduledInterval,
                rescheduledStart: rescheduledInterval?.start ?? null,
                rescheduledEnd: rescheduledInterval?.end ?? null,

                onShow: (value) => setShown(value),
                onAmenityIdChange: (value) => setRescheduledAmenityId(value),
                onDateChange: (value) => setRescheduledDate(value),
                onBoundaryChange: (value) => setLocalBoundary(value),
                onRescheduledIntervalChange: (newInterval) => {
                    setRescheduledInterval(newInterval)
                },

                onRemove: handleRemove,
                onConfirm: handleConfirm,
            }}
        >
            {children}
        </ReschedulerFormContext.Provider>
    </>);
};

ReschedulerFormProvider.propTypes = {};

ReschedulerFormProvider.defaultProps = {};

export default ReschedulerFormProvider;
